import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseStorage';

export interface UserAnalytics {
  hy_user_id: string;
  hy_name: string;
  hy_country: string;
  hy_country_code: string;
  created_at: string;
  total_assets: number;
}

export interface AnalyticsSummary {
  total_users: number;
  total_assets: number;
  total_countries: number;
  total_ai_generated: number;
  total_uploaded: number;
}

export const useAnalytics = () => {
  const [users, setUsers] = useState<UserAnalytics[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary>({
    total_users: 0,
    total_assets: 0,
    total_countries: 0,
    total_ai_generated: 0,
    total_uploaded: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📊 [ANALYTICS] Starting analytics data fetch');
      
      // Fetch all users from users table
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select(`
          hy_user_id,
          hy_name,
          hy_country,
          hy_country_code,
          created_at
        `)
        .order('created_at', { ascending: false });

      if (usersError) {
        console.error('❌ [ANALYTICS] Users fetch error:', usersError);
        throw usersError;
      }

      console.log(`✅ [ANALYTICS] Fetched ${usersData?.length || 0} users`);
      
      // Get asset counts for each user individually
      const usersWithCounts: UserAnalytics[] = [];
      for (const user of usersData || []) {
        const { count } = await supabase
          .from('assets')
          .select('id', { count: 'exact', head: true })
          .eq('hy_user_id', user.hy_user_id);

        usersWithCounts.push({
          ...user,
          total_assets: count || 0
        });
      }

      console.log(`📊 [ANALYTICS] Processed asset counts for ${usersWithCounts.length} users`);
      setUsers(usersWithCounts);

      // Only fetch assets if resetAssets is true (initial load)
      const totalUsers = usersWithCounts.length;
      const totalAssets = usersWithCounts.reduce((sum, user) => sum + user.total_assets, 0);
      const uniqueCountries = new Set(usersWithCounts.map(u => u.hy_country_code).filter(Boolean)).size;

      // Get AI generated and uploaded counts
      const { count: aiGeneratedCount } = await supabase
        .from('assets')
        .select('id', { count: 'exact', head: true })
        .eq('isaigenerated', true);
      
      const { count: uploadedCount } = await supabase
        .from('assets')
        .select('id', { count: 'exact', head: true })
        .eq('isaigenerated', false);


      setSummary({
        total_users: totalUsers,
        total_assets: totalAssets,
        total_countries: uniqueCountries,
        total_ai_generated: aiGeneratedCount || 0,
        total_uploaded: uploadedCount || 0
      });

    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchAnalytics();
  }, []);

  return { 
    users, 
    summary, 
    loading, 
    error, 
    refetch: fetchAnalytics
  };
};